import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import Service from "@/services/SuperAdmin";

export interface SuperAdminOverview {
  generated_at: string;
  users: {
    total_active: number;
    super_admin: number;
    admin: number;
    user: number;
    supervisor_mutu_ppi: number;
    tanpa_role: number;
  };
  visits_today: { ralan: number; igd: number; ranap: number; total: number };
  census: { admitted: number; capacity: number; bor_percent: number | null };
  logins: {
    success_today: number;
    failed_today: number;
    recent: Array<{
      username: string;
      nama: string;
      ip: string;
      activity: string;
      success: boolean;
      status: string;
      attempted_at: string | null;
    }>;
  };
  audit: { mutations_today: number };
  trend: Array<{
    date: string;
    label: string;
    ralan: number;
    igd: number;
    ranap: number;
  }>;
}

function triggerDownload(res: any, fallbackName: string) {
  const disposition: string = res.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : fallbackName;

  const blob = new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "superadmin"
})
class SuperAdminModule extends VuexModule {
  overview: SuperAdminOverview | null = null;
  loading = false;
  exporting = false;

  @Mutation
  SET_OVERVIEW(v: SuperAdminOverview | null) {
    this.overview = v;
  }

  @Mutation
  SET_LOADING(v: boolean) {
    this.loading = v;
  }

  @Mutation
  SET_EXPORTING(v: boolean) {
    this.exporting = v;
  }

  @Action
  async loadOverview() {
    this.SET_LOADING(true);
    try {
      const res = await Service.overview();
      this.SET_OVERVIEW(res.data);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action
  async exportUsers(format: string) {
    this.SET_EXPORTING(true);
    try {
      const res = await Service.exportUsers(format);
      triggerDownload(res, `superadmin-users.${format}`);
    } finally {
      this.SET_EXPORTING(false);
    }
  }

  @Action
  async exportLogins(payload: { format: string; params?: Record<string, any> }) {
    this.SET_EXPORTING(true);
    try {
      const res = await Service.exportLogins(payload.format, payload.params || {});
      triggerDownload(res, `superadmin-login-logs.${payload.format}`);
    } finally {
      this.SET_EXPORTING(false);
    }
  }

  @Action
  async exportCensus(format: string) {
    this.SET_EXPORTING(true);
    try {
      const res = await Service.exportCensus(format);
      triggerDownload(res, `superadmin-sensus-ranap.${format}`);
    } finally {
      this.SET_EXPORTING(false);
    }
  }

  @Action
  async exportVisits(payload: { format: string; params?: Record<string, any> }) {
    this.SET_EXPORTING(true);
    try {
      const res = await Service.exportVisits(payload.format, payload.params || {});
      triggerDownload(res, `superadmin-kunjungan.${payload.format}`);
    } finally {
      this.SET_EXPORTING(false);
    }
  }

  @Action
  async exportAudit(payload: { format: string; params?: Record<string, any> }) {
    this.SET_EXPORTING(true);
    try {
      const res = await Service.exportAudit(payload.format, payload.params || {});
      triggerDownload(res, `superadmin-audit.${payload.format}`);
    } finally {
      this.SET_EXPORTING(false);
    }
  }
}

export default getModule(SuperAdminModule);
